'use client';

import { Button } from '@modules/common/components/button';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import { Text } from '@modules/common/components/text';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/navigation';
import { startTransition, useEffect } from 'react';

type MainErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

// Generic backstop error boundary for the `(main)` route group. Without it,
// any error thrown while rendering a page in this group bubbles to the
// app-level `global-error`, which blanks the whole page. This keeps the
// header/footer shell and shows a recoverable state instead.
//
// Note: this only catches errors thrown during render. It does not help with
// a fetch that stalls without ever rejecting (e.g. sections that fetch via a
// Server Action in `useEffect` and swallow their own errors) — those never
// throw into this boundary.
export default function MainError({ error, reset }: MainErrorProps) {
  const router = useRouter();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  function handleRetry() {
    // Batch into one transition so reset() doesn't re-render the boundary
    // before the refreshed RSC payload from router.refresh() has arrived.
    startTransition(() => {
      router.refresh();
      reset();
    });
  }

  return (
    <section className="content-container flex flex-col items-center justify-center min-h-[60vh] gap-4 py-16 text-center">
      <h1 className="font-heading nw-typography-heading-lg font-light text-fg-neutral-strong">
        Something went wrong
      </h1>
      <Text className="text-fg-neutral-weak max-w-md">
        We couldn&apos;t load this page. Please try again or browse our
        treatments.
      </Text>
      <div className="flex gap-3">
        <Button variant="secondary" onClick={handleRetry}>
          Try again
        </Button>
        <Button asChild>
          <LocalizedClientLink href="/conditions">
            Browse treatments
          </LocalizedClientLink>
        </Button>
      </div>
    </section>
  );
}
